import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useNewsFilters } from '~/react-query-hooks/news/useNewsFilter';
import { NewsFilter } from '~/services/news/news.types';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import { useNewsUrlStore } from '~/views/news/stores/use-news-url-store/useNewsUrlStore';
import {
  applySelectionMapping,
  countFilterKeyChecked,
  createAccordionFilters,
  toggleSelectionMapping,
} from '~/views/news/utils/news-filters-utils/news-filters-utils';

import { ClearFiltersButton } from '../clear-filters-button/ClearFiltersButton';
import { HighlightsFilters } from '../highlights-filters/HighlightsFilters';
import { NewsList } from '../news-list/NewsList';
import { SideAccordionFilters } from '../side-accordion-filters/SideAccordionFilters';
import styles from './NewsView.module.scss';

export interface NewsViewProps {
  entity?: Entity;
  includedFilterKeys?: NewsFilter['key'][];
  trackingSource: PosthogTrackingSource;
}

export const NewsView = ({
  entity,
  includedFilterKeys,
  trackingSource,
}: NewsViewProps) => {
  const { t } = useTranslation('news');
  const { lists } = useWatchlist();
  const { data: serverFilters = [] } = useNewsFilters();

  const { trackNewsFilter } = usePosthogEvents();
  const {
    newsMostRead,
    aOMostReadNews,
    filtersMappingFromUrl,
    setMostReadParam,
    setAOMostReadNews,
    setUrlFilters,
    resetAll,
  } = useNewsUrlStore(s => ({
    newsMostRead: s.newsMostRead === '1',
    aOMostReadNews: s.aOMostReadNews === '1',
    filtersMappingFromUrl: s.newsFilters,
    setAOMostReadNews: s.setAOMostReadNews,
    setMostReadParam: s.setMostReadParam,
    setUrlFilters: s.setUrlFilters,
    resetAll: s.resetAll,
  }));

  const baseFilters = createAccordionFilters(serverFilters, lists);
  const filters = applySelectionMapping(
    baseFilters,
    filtersMappingFromUrl || {}
  );

  const newsFilters = includedFilterKeys
    ? filters.filter(f => includedFilterKeys.includes(f.key))
    : filters;

  const initialDataFilters = entity
    ? [
        {
          key: 'entities',
          name: 'Entity',
          options: [
            {
              isChecked: true,
              visible: true,
              key: entity.tag,
              name: entity.name,
            },
          ],
        },
      ]
    : [];
  const newsListFilters = [...initialDataFilters, ...newsFilters];

  const showResetButton =
    !newsMostRead ||
    newsFilters.some(
      f => countFilterKeyChecked({ filters, filterKey: f.key }).some
    );

  return (
    <>
      {entity && (
        <p className={styles.listTitle}>
          {entity.name} {t('news:title')}
        </p>
      )}
      <div className={styles.newsViewRoot} data-testid="news-lists-container">
        <div className={styles.leftContent}>
          <NewsList
            filters={newsListFilters}
            isFetchMostRead={entity ? aOMostReadNews : newsMostRead}
            trackingSource={trackingSource}
          />
        </div>
        <div className={styles.rightContent}>
          <HighlightsFilters
            isFetchMostRead={entity ? aOMostReadNews : newsMostRead}
            toggleIsFetchMostRead={isToggled => {
              if (isToggled) {
                trackNewsFilter('news', 'most_read');
              }

              entity
                ? setAOMostReadNews(isToggled)
                : setMostReadParam(isToggled);
            }}
          />
          <SideAccordionFilters
            filters={newsFilters}
            toggleFilterOption={(filterKey, optionKey, track) => {
              track?.();
              setUrlFilters(
                toggleSelectionMapping({
                  current: filtersMappingFromUrl || {},
                  filters: newsFilters,
                  filterKey,
                  optionKey,
                })
              );
            }}
          />
          {showResetButton && (
            <ClearFiltersButton
              data-testid="reset-all-filters"
              onClick={resetAll}
            />
          )}
        </div>
      </div>
    </>
  );
};
